/* eslint-disable no-restricted-imports */
import * as React from 'react';

import { Heading, Text } from '@/components/ui';
import { WM_FLAT_SURFACE_CLASSNAME } from '@/components/ui/surfaceStyles';

const Rule = ({ children, title }) => (
    <section className={`${WM_FLAT_SURFACE_CLASSNAME} tw-space-y-3 tw-rounded-3xl tw-p-6 lg:tw-p-8`}>
        <Heading as="h3" variant="title">{title}</Heading>
        {children}
    </section>
);

const Do = ({ children }) => (
    <div className="tw-rounded-2xl tw-border tw-border-success tw-bg-success-surface tw-p-4">
        <Text tone="muted" variant="overline">Do</Text>
        <div className="tw-mt-2">
            <Text variant="body-sm">{children}</Text>
        </div>
    </div>
);

const Dont = ({ children }) => (
    <div className="tw-rounded-2xl tw-border tw-border-destructive tw-bg-destructive-surface tw-p-4">
        <Text tone="muted" variant="overline">Don&apos;t</Text>
        <div className="tw-mt-2">
            <Text variant="body-sm">{children}</Text>
        </div>
    </div>
);

const DoDontGrid = ({ children }) => (
    <div className="tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-2">
        {children}
    </div>
);

export default function RulesFoundation() {
    return (
        <div className="tw-space-y-8">
            <header className="tw-space-y-2">
                <Text tone="muted" variant="overline">Foundation</Text>
                <Heading as="h2" variant="title-lg">Rules</Heading>
                <Text as="p" tone="muted" variant="body">
                    A short list of invariants that keep the design system coherent. Every
                    rule below is enforced either by a lint rule, a visual regression test,
                    or a code review convention.
                </Text>
            </header>

            <Rule title="1. Styling lives inside src/components/">
                <Text as="p" variant="body">
                    Application code imports primitives — it never reaches for colour,
                    border, radius, shadow or typography utilities directly. Layout
                    utilities (margin, padding, gap, grid, flex, width, height) are the
                    only Tailwind classes allowed outside <code>src/components/</code>.
                </Text>
                <DoDontGrid>
                    <Do>
                        Compose <code>&lt;Surface&gt;</code>, <code>&lt;Text variant=&quot;body&quot;&gt;</code>,
                        and layout classes like <code>tw-flex tw-gap-4</code>.
                    </Do>
                    <Dont>
                        Reach for <code>tw-bg-white</code>, <code>tw-text-slate-500</code>,
                        <code>tw-rounded-xl</code>, or <code>tw-shadow-md</code> in a page or
                        feature file — these are design-system concerns.
                    </Dont>
                </DoDontGrid>
            </Rule>

            <Rule title="2. Colours go through named semantic tokens">
                <Text as="p" variant="body">
                    Every colour in the UI resolves to one of the named semantic tokens
                    listed in the Colors foundation — Surface, Brand families (primary,
                    secondary) and Feedback families (info, success, warning, destructive).
                    Raw palette utilities like <code>tw-bg-slate-100</code> or
                    <code> tw-text-red-500</code> are disallowed — the
                    <code> no-raw-color-classes</code> rule flags them on sight.
                </Text>
                <DoDontGrid>
                    <Do>
                        Reach for a token: <code>tw-bg-info-surface</code> for a tinted
                        fill, <code>tw-border-info-border</code> for the soft border that
                        pairs with it.
                    </Do>
                    <Dont>
                        Use opacity modifiers as a proxy for tints —
                        <code> tw-border-primary/20</code>, <code>tw-bg-foreground/10</code>.
                        If you need a softer shade, declare it as a token in the catalog
                        first.
                    </Dont>
                </DoDontGrid>
            </Rule>

            <Rule title="3. Hover-darken is the only sanctioned opacity modifier">
                <Text as="p" variant="body">
                    The single permitted use of <code>tw-{`{prop}`}-{`{color}`}/{`{N}`}</code> outside
                    the Colors catalog is the hover-darken pattern on solid fills —
                    <code> hover:tw-bg-primary/90</code>, <code>hover:tw-bg-destructive/80</code>,
                    and so on. This is a state transformation, not a colour choice, so it
                    does not require a dedicated token. Every other use of opacity-as-shade
                    must become a named token first — including overlays, where alpha is
                    baked into <code>--overlay-strong</code> and <code>--overlay-soft</code>.
                </Text>
                <DoDontGrid>
                    <Do>
                        Darken a solid button on hover with
                        <code> hover:tw-bg-primary/90</code>. The base colour
                        (<code>tw-bg-primary</code>) is the token; the modifier is just a
                        state delta. For text hierarchy, switch tokens
                        (<code>tw-text-foreground</code> ↔ <code>tw-text-muted-foreground</code>)
                        rather than reaching for opacity.
                    </Do>
                    <Dont>
                        Use <code>/N</code> as a stand-in for "I want a softer version of
                        this colour" or "this text should be a bit dimmer". That is what
                        <code> {`{color}`}-surface</code>, <code>{`{color}`}-border</code>,
                        <code> overlay-soft</code>, and the muted-foreground token are for.
                    </Dont>
                </DoDontGrid>
            </Rule>

            <Rule title="4. Typography is variant-driven">
                <Text as="p" variant="body">
                    Use <code>&lt;Heading&gt;</code> and <code>&lt;Text&gt;</code> with a
                    named variant. Font size, weight, line-height and letter-spacing are
                    all encoded by the variant — never mix them via utilities. The
                    <code> no-typography-tailwind</code> rule disallows <code>tw-text-lg</code>,
                    <code>tw-font-semibold</code>, <code>tw-leading-6</code>, etc. outside
                    of <code>src/components/</code>.
                </Text>
            </Rule>

            <Rule title="5. Spacing prefers named tokens, then the base scale">
                <Text as="p" variant="body">
                    If a dimension recurs across the product (layout columns, modal sizes,
                    preview boxes) it becomes a named token in <code>tailwind.config.js</code>.
                    For one-off spacing, the default 4 px scale is the next step. Arbitrary
                    values like <code>tw-w-[317px]</code> are rejected — they are a signal
                    that a token is missing, not that the rule should be bypassed.
                </Text>
            </Rule>

            <Rule title="6. Interactive and motion styles come from primitives">
                <Text as="p" variant="body">
                    Hover, focus, disabled, and transition behaviour live inside the
                    primitive. Feature code should not manage <code>hover:</code>,
                    <code>focus-visible:</code>, or <code>tw-transition-*</code> classes —
                    those decisions belong to a single source of truth. The
                    <code> no-interactive-tailwind</code> and <code>no-motion-tailwind</code>
                    rules enforce this.
                </Text>
            </Rule>

            <Rule title="7. Z-index is semantic, not numeric">
                <Text as="p" variant="body">
                    Use <code>tw-z-lift</code>, <code>tw-z-modal</code>,
                    <code>tw-z-overlay</code>, and <code>tw-z-notification</code> —
                    never <code>tw-z-10</code>, <code>tw-z-50</code>, or arbitrary values.
                    The four semantic rungs cover every stacking need in the product.
                </Text>
            </Rule>

            <Rule title="8. The catalog is the contract">
                <Text as="p" variant="body">
                    Every primitive has at least one story in the Components tab. If a
                    consumer needs a variant that doesn&apos;t exist, add a story first —
                    then the prop, then the consumer. Visual regression snapshots pick up
                    accidental behaviour changes before they ship.
                </Text>
            </Rule>
        </div>
    );
}
